import { useState } from 'react';
import type { WizardFormData } from '../page';

interface Props {
  formData: WizardFormData;
  onChange: (updates: Partial<WizardFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  loading?: boolean;
  error?: string;
}

const currencies = [
  { value: 'BRL', label: 'Real Brasileiro (R$)' },
  { value: 'USD', label: 'Dólar Americano ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'Libra Esterlina (£)' },
];

const timezones = [
  { value: 'America/Sao_Paulo', label: 'Brasília (UTC-3)' },
  { value: 'America/Manaus', label: 'Manaus (UTC-4)' },
  { value: 'America/Belem', label: 'Belém (UTC-3)' },
  { value: 'America/Fortaleza', label: 'Fortaleza (UTC-3)' },
  { value: 'America/Recife', label: 'Recife (UTC-3)' },
  { value: 'America/Cuiaba', label: 'Cuiabá (UTC-4)' },
  { value: 'America/Porto_Velho', label: 'Porto Velho (UTC-4)' },
  { value: 'America/Rio_Branco', label: 'Rio Branco (UTC-5)' },
  { value: 'America/Noronha', label: 'Fernando de Noronha (UTC-2)' },
];

const maskCpf = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const maskPhone = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

const isValidCpf = (cpf: string): boolean => {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;
  const calc = (n: number) => {
    let sum = 0;
    for (let i = 0; i < n - 1; i++) sum += parseInt(d[i]) * (n - i);
    const rem = (sum * 10) % 11;
    return rem >= 10 ? 0 : rem;
  };
  return calc(10) === parseInt(d[9]) && calc(11) === parseInt(d[10]);
};

export const WizardStep2 = ({ formData, onChange, onNext, onBack, loading = false, error }: Props) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!formData.firstName.trim()) errs.firstName = 'Nome é obrigatório';
    if (!formData.lastName.trim()) errs.lastName = 'Sobrenome é obrigatório';
    if (formData.cpf && formData.cpf.replace(/\D/g, '').length > 0) {
      if (!isValidCpf(formData.cpf)) errs.cpf = 'CPF inválido';
    }
    if (formData.phone && formData.phone.replace(/\D/g, '').length > 0) {
      const raw = formData.phone.replace(/\D/g, '');
      if (raw.length < 10) errs.phone = 'Telefone incompleto';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validate()) return;
    onNext();
  };

  const inputCls = (field: string) =>
    `w-full pl-10 pr-4 h-12 border rounded-xl text-sm text-gray-800 outline-none transition-all placeholder:text-gray-400 disabled:opacity-60 ${
      errors[field]
        ? 'border-red-300 focus:ring-2 focus:ring-red-100'
        : 'border-gray-200 focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400'
    }`;

  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-700 text-gray-900 mb-1">Perfil pessoal</h2>
        <p className="text-gray-500 text-sm">Seus dados para personalizar a experiência</p>
      </div>

      {error && (
        <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
          <i className="ri-error-warning-line text-red-500 text-base mt-0.5 shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <div className="space-y-4">
        {/* Nome + Sobrenome */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-500 text-gray-700 mb-1.5">
              Nome <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => {
                  onChange({ firstName: e.target.value });
                  if (errors.firstName) setErrors((p) => ({ ...p, firstName: '' }));
                }}
                placeholder="Seu primeiro nome"
                disabled={loading}
                className={inputCls('firstName')}
              />
            </div>
            {errors.firstName && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line text-xs" />{errors.firstName}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-500 text-gray-700 mb-1.5">
              Sobrenome <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => {
                  onChange({ lastName: e.target.value });
                  if (errors.lastName) setErrors((p) => ({ ...p, lastName: '' }));
                }}
                placeholder="Seu sobrenome"
                disabled={loading}
                className={inputCls('lastName')}
              />
            </div>
            {errors.lastName && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line text-xs" />{errors.lastName}
              </p>
            )}
          </div>
        </div>

        {/* CPF + Telefone */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-500 text-gray-700 mb-1.5">
              CPF
              <span className="ml-1 text-xs text-gray-400 font-400">(opcional)</span>
            </label>
            <div className="relative">
              <i className="ri-id-card-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="text"
                value={maskCpf(formData.cpf)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  onChange({ cpf: raw });
                  if (errors.cpf) setErrors((p) => ({ ...p, cpf: '' }));
                }}
                placeholder="000.000.000-00"
                maxLength={14}
                disabled={loading}
                className={inputCls('cpf')}
              />
            </div>
            {errors.cpf && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line text-xs" />{errors.cpf}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-500 text-gray-700 mb-1.5">
              Telefone
              <span className="ml-1 text-xs text-gray-400 font-400">(opcional)</span>
            </label>
            <div className="relative">
              <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="tel"
                value={maskPhone(formData.phone)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  onChange({ phone: raw });
                  if (errors.phone) setErrors((p) => ({ ...p, phone: '' }));
                }}
                placeholder="(11) 99999-0000"
                maxLength={15}
                disabled={loading}
                className={inputCls('phone')}
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line text-xs" />{errors.phone}
              </p>
            )}
          </div>
        </div>

        {/* Moeda */}
        <div>
          <label className="block text-sm font-500 text-gray-700 mb-1.5">Moeda padrão</label>
          <div className="relative">
            <i className="ri-money-dollar-circle-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
            <select
              value={formData.currency}
              onChange={(e) => onChange({ currency: e.target.value })}
              disabled={loading}
              className="w-full pl-10 pr-10 h-12 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 bg-white appearance-none cursor-pointer transition-all disabled:opacity-60"
            >
              {currencies.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />
          </div>
        </div>

        {/* Fuso horário */}
        <div>
          <label className="block text-sm font-500 text-gray-700 mb-1.5">Fuso horário</label>
          <div className="relative">
            <i className="ri-time-zone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
            <select
              value={formData.timezone}
              onChange={(e) => onChange({ timezone: e.target.value })}
              disabled={loading}
              className="w-full pl-10 pr-10 h-12 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 bg-white appearance-none cursor-pointer transition-all disabled:opacity-60"
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />
          </div>
        </div>

        {/* Info box */}
        <div className="flex items-start gap-2.5 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-100">
          <i className="ri-shield-check-line text-emerald-500 text-base shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-700 leading-relaxed">
            Seus dados são armazenados com segurança e só podem ser vistos por você.
            CPF e telefone são usados apenas para identificação e nunca compartilhados.
          </p>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={onBack}
          disabled={loading}
          className="h-12 px-6 border border-gray-200 text-gray-700 font-600 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          Voltar
        </button>
        <button
          onClick={handleNext}
          disabled={loading}
          className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {loading ? (
            <><i className="ri-loader-4-line animate-spin text-base" />Salvando...</>
          ) : (
            <>Continuar<i className="ri-arrow-right-line text-base" /></>
          )}
        </button>
      </div>
    </div>
  );
};
